import React, { useEffect, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { 
  MoreVertical, 
  MessageCircle, 
  Archive,
  Loader2,
  RefreshCw
} from 'lucide-react';
import { Chat } from '../../types';
import { useChatStore } from '../../stores/chatStore';
import { ProgressBar } from '../common/ProgressBar';
import { ChatLoadingProgress } from '../common/ChatLoadingProgress';
import { TripFilterPanel } from './TripFilterPanel';
import { useDebounce } from '../../hooks/useDebounce';
import toast from 'react-hot-toast';

interface ChatListProps {
  onChatSelect: (chat: Chat) => void;
  activeChatId?: number;
}

export const ChatList: React.FC<ChatListProps> = ({ onChatSelect, activeChatId }) => {
  const { 
    chats, 
    loadChats, 
    isLoading, 
    error, 
    isPolling,
    currentSearchQuery,
    currentFilterType,
    setSearchQuery,
    setFilterType,
    loadingChatId,
    selectedTripIds,
    loadSelectedTripIds,
    saveSelectedTripIds,
    isBulkLoading,
    bulkLoadingProgress,
    bulkLoadingTotal,
    bulkLoadingCurrent,
    bulkLoadingMessage
  } = useChatStore();
  const [showFilters] = useState(true);
  const [showTripFilterPanel, setShowTripFilterPanel] = useState(false);

  // Debounced search query for API calls
  const debouncedSearchQuery = useDebounce(currentSearchQuery, 300);

  // Load saved trip IDs on component mount
  useEffect(() => {
    loadSelectedTripIds();
  }, [loadSelectedTripIds]);

  // Load chats when search query, filter type, or trip IDs change (debounced)
  useEffect(() => {
    loadChats(debouncedSearchQuery, currentFilterType, selectedTripIds);
  }, [loadChats, debouncedSearchQuery, currentFilterType, selectedTripIds]);

  const formatLastMessageTime = (timestamp: number) => {
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
    } catch {
      return '';
    }
  };

  const getChatName = (chat: Chat) => {
    return chat.name;
  };

  const getChatSubtitle = (chat: Chat) => {
    return chat.tripName;
  };

  const handleTripFilterSave = (tripIds: string[]) => {
    saveSelectedTripIds(tripIds);
    setShowTripFilterPanel(false);
  };

  const handleClearFilters = () => {
    saveSelectedTripIds([]);
    setSearchQuery('');
    setFilterType('ALL');
  };

  const handleCopyUserId = async (userId: number, event: React.MouseEvent) => {
    event.preventDefault(); // Prevent default button behavior
    event.stopPropagation(); // Prevent chat selection when clicking user ID
    
    try {
      await navigator.clipboard.writeText(userId.toString());
      toast.success(`User ID ${userId} copied to clipboard!`);
    } catch (error) {
      console.error('Failed to copy user ID:', error);
      toast.error('Failed to copy user ID');
    }
  };

  if (error) {
    return (
      <div className="h-full flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-red-600 mb-2">Failed to load chats</p>
          <button 
            onClick={() => loadChats()}
            className="text-sm text-primary-600 hover:text-primary-500"
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white border-r border-gray-200">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <h2 className="text-lg font-semibold text-gray-900">Chats</h2>
            {isPolling && (
              <div className="flex items-center space-x-1 text-xs text-green-600">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span>Live</span>
              </div>
            )}
          </div>
          <div className="flex items-center space-x-2">
            {/* Refresh Button */}
            <button
              onClick={() => {
                loadChats(currentSearchQuery, currentFilterType, selectedTripIds, true);
              }}
              disabled={isLoading}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Refresh chats"
              title="Refresh chats"
            >
              <RefreshCw className={`h-5 w-5 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            {/* Plus Icon - Hidden */}
            {/* <button
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="New chat"
            >
              <Plus className="h-5 w-5" />
            </button> */}
            <div className="relative">
              <button
                onClick={() => {
                  setShowTripFilterPanel(true);
                }}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Filter by trips"
              >
                <MoreVertical className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Search and Filter */}
        {showFilters && (
          <div className="space-y-3">
            <div className="relative">
              {/* Search Icon - Hidden */}
              {/* <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-gray-400" />
              </div> */}
              <input
                type="text"
                placeholder="Search chats..."
                value={currentSearchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                aria-label="Search chats"
              />
            </div>
            
            {/* Chat Loading Progress */}
            <ChatLoadingProgress
              isVisible={isBulkLoading}
              progress={bulkLoadingProgress}
              total={bulkLoadingTotal}
              current={bulkLoadingCurrent}
              message={bulkLoadingMessage}
            />
            
            {/* Filter Dropdown - Hidden */}
            {/* <div className="relative">
              <select
                value={currentFilterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent appearance-none bg-white"
                aria-label="Filter chats"
              >
                <option value="ALL">All Chats</option>
                <option value="PENDING">Pending</option>
                <option value="QUALIFIED">Qualified</option>
                <option value="UNREAD">Unread</option>
              </select>
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <Filter className="h-4 w-4 text-gray-400" />
              </div>
            </div> */}
          </div>
        )}
      </div>

      {/* Chat List */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center p-8">
            <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : chats.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-8 text-center">
            <MessageCircle className="h-12 w-12 text-gray-300 mb-4" />
            <p className="text-gray-500 mb-2">
              {currentSearchQuery ? 'No chats found' : 'No chats yet'}
            </p>
            {!currentSearchQuery && (
              <p className="text-sm text-gray-400">
                Start a conversation with your team
              </p>
            )}
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {chats.map((chat) => (
              <button
                key={chat.userId}
                onClick={() => onChatSelect(chat)}
                className={`w-full p-4 text-left hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition-colors relative ${
                  activeChatId === chat.userId ? 'bg-primary-50 border-r-2 border-primary-500' : ''
                } ${loadingChatId === chat.userId ? 'opacity-75' : ''}`}
                aria-label={`Open chat with ${getChatName(chat)}`}
                disabled={loadingChatId === chat.userId}
              >
                {/* Progress bar for loading state */}
                <ProgressBar 
                  isLoading={loadingChatId === chat.userId} 
                  className="absolute top-0 left-0 right-0"
                  height="sm"
                />
                
                <div className="flex items-start space-x-3">
                  {/* Avatar */}
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                      {loadingChatId === chat.userId ? (
                        <Loader2 className="h-5 w-5 text-primary-600 animate-spin" />
                      ) : (
                        <MessageCircle className="h-5 w-5 text-primary-600" />
                      )}
                    </div>
                  </div>

                  {/* Chat Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-medium text-gray-900 truncate">
                          {getChatName(chat)}
                        </h3>
                        <span
                          onClick={(e) => handleCopyUserId(chat.userId, e)}
                          onMouseDown={(e) => e.stopPropagation()}
                          className="text-xs text-gray-400 mt-0.5 hover:text-primary-600 hover:underline transition-colors cursor-pointer inline-block"
                          title={`Click to copy User ID: ${chat.userId}`}
                        >
                          ID: {chat.userId}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2 ml-2">
                        {chat.unreadCount > 0 && (
                          <span className="inline-flex items-center justify-center h-5 w-5 text-xs font-medium text-white bg-primary-600 rounded-full">
                            {chat.unreadCount > 99 ? '99+' : chat.unreadCount}
                          </span>
                        )}
                        <span className="text-xs text-gray-500">
                          {formatLastMessageTime(chat.lastMessageTime)}
                        </span>
                      </div>
                    </div>

                    <p className="text-sm text-gray-600 truncate mb-1">
                      {getChatSubtitle(chat)}
                    </p>

                    <p className="text-sm text-gray-500 truncate">
                      <span className="font-medium">
                        {chat.lastMessageBy}: 
                      </span>
                      {chat.lastMessage}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Empty state */}
        {chats.length === 0 && !isLoading && (
          <div className="flex flex-col items-center justify-center h-full text-center p-8">
            <MessageCircle className="h-12 w-12 text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {selectedTripIds.length > 0 ? 'No conversations for selected trips' : 'No conversations yet'}
            </h3>
            <p className="text-gray-500 mb-4">
              {selectedTripIds.length > 0 
                ? 'Try selecting different trips or clear filters to see all conversations'
                : 'Start a conversation to see it here'
              }
            </p>
            {selectedTripIds.length > 0 ? (
              <button 
                onClick={handleClearFilters}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              >
                Clear Filters
              </button>
            ) : (
              <button className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
                New Chat
              </button>
            )}
          </div>
        )}
      </div>

      {/* Footer Actions */}
      <div className="p-4 border-t border-gray-200">
        <button className="w-full flex items-center justify-center space-x-2 text-sm text-gray-600 hover:text-gray-800 py-2 rounded-lg hover:bg-gray-50 transition-colors">
          <Archive className="h-4 w-4" />
          <span>Archived Chats</span>
        </button>
      </div>

      {/* Trip Filter Panel */}
      <TripFilterPanel
        isOpen={showTripFilterPanel}
        onClose={() => setShowTripFilterPanel(false)}
        onSave={handleTripFilterSave}
        initialSelectedTripIds={selectedTripIds}
      />
    </div>
  );
};
